import React, { useState, useEffect } from 'react';
import './App.css';
import {
  Header,
  HeroSection,
  AIStorySection,
  FavoriteStoriesSection,
  AnimatedStoriesSection,
  AppFeaturesSection,
  TestimonialsSection,
  FAQSection,
  SupportersSection
} from './components';
import SimplifiedFooter from './SimplifiedFooter';
import AdminLogin from './AdminLogin';
import AdminDashboard from './AdminDashboard';
import LullabySection from './LullabySection';
import PrivateAccessGate from './PrivateAccessGate';
import InvestorsLogin from './InvestorsLogin';
import InvestorsPortal from './InvestorsPortal';
import { useAnalytics } from './useAnalytics';

function App() {
  const [showAdmin, setShowAdmin] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [hasPrivateAccess, setHasPrivateAccess] = useState(false);
  const [showInvestors, setShowInvestors] = useState(false);
  const [isInvestorLoggedIn, setIsInvestorLoggedIn] = useState(false);
  
  // Initialize analytics tracking
  useAnalytics();

  // Validate investors token
  const validateInvestorsToken = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verify-investors-token`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      return data.valid;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  };

  // Check if admin is already logged in and private access is granted
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAdminLoggedIn(true);
    }

    const investorsToken = localStorage.getItem('investorsToken');
    if (investorsToken) {
      // Validate the investors token before auto-login
      validateInvestorsToken(investorsToken).then((isValid) => {
        if (isValid) {
          setIsInvestorLoggedIn(true);
        } else {
          // Token is invalid, remove it
          localStorage.removeItem('investorsToken');
          console.log('Removed invalid investors token from localStorage');
        }
      });
    }

    checkPrivateAccess();
    
    // Check private access settings every 30 seconds
    const interval = setInterval(checkPrivateAccess, 30000);

    // Listen for investors access event
    const handleOpenInvestors = () => {
      setShowInvestors(true);
    };

    window.addEventListener('openInvestors', handleOpenInvestors);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('openInvestors', handleOpenInvestors);
    };
  }, []);

  const checkPrivateAccess = async () => {
    try {
      // First check if private access is enabled on the server
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/private-access-settings`);
      if (response.ok) {
        const data = await response.json();
        
        console.log('Private access settings:', data);
        
        if (!data.is_enabled) {
          // Private access is disabled, grant access directly
          console.log('Private access disabled, granting access');
          setHasPrivateAccess(true);
          return;
        } else {
          console.log('Private access enabled, checking localStorage');
        }
        
        // Private access is enabled, check localStorage
        const siteAccess = localStorage.getItem('siteAccess');
        const accessTimestamp = localStorage.getItem('accessTimestamp');
        const storedSessionId = localStorage.getItem('sessionId');
        
        console.log('localStorage siteAccess:', siteAccess, 'timestamp:', accessTimestamp, 'sessionId:', storedSessionId);
        console.log('Current server sessionId:', data.session_id);
        
        if (siteAccess === 'granted' && accessTimestamp && storedSessionId) {
          // Check if session_id matches (to invalidate old sessions)
          if (storedSessionId !== data.session_id) {
            console.log('Session ID mismatch, invalidating access');
            localStorage.removeItem('siteAccess');
            localStorage.removeItem('accessTimestamp');
            localStorage.removeItem('sessionId');
            setHasPrivateAccess(false);
            return;
          }
          
          // Vérifier que l'accès n'est pas expiré (24h)
          const now = Date.now();
          const accessTime = parseInt(accessTimestamp);
          const oneDay = 24 * 60 * 60 * 1000; // 24 heures en millisecondes
          
          if (now - accessTime < oneDay) {
            console.log('Valid access token found, keeping access');
            setHasPrivateAccess(true);
          } else {
            // Accès expiré, nettoyer le localStorage
            console.log('Access token expired, removing access');
            localStorage.removeItem('siteAccess');
            localStorage.removeItem('accessTimestamp');
            localStorage.removeItem('sessionId');
            setHasPrivateAccess(false);
          }
        } else {
          console.log('No valid access token, requiring authentication');
          setHasPrivateAccess(false);
        }
      }
    } catch (error) {
      console.error('Error checking private access:', error);
      // En cas d'erreur, vérifier seulement le localStorage
      const siteAccess = localStorage.getItem('siteAccess');
      const accessTimestamp = localStorage.getItem('accessTimestamp');
      
      if (siteAccess === 'granted' && accessTimestamp) {
        const now = Date.now();
        const accessTime = parseInt(accessTimestamp);
        const oneDay = 24 * 60 * 60 * 1000;
        
        if (now - accessTime < oneDay) {
          setHasPrivateAccess(true);
        } else {
          localStorage.removeItem('siteAccess');
          localStorage.removeItem('accessTimestamp');
          localStorage.removeItem('sessionId');
          setHasPrivateAccess(false);
        }
      } else {
        setHasPrivateAccess(false);
      }
    }
  };

  const handleAdminAccess = () => {
    setShowAdmin(true);
  };

  const handleAdminLogin = () => {
    setIsAdminLoggedIn(true);
    setShowAdmin(false);
  };

  const handleAdminLogout = () => {
    setIsAdminLoggedIn(false);
    setShowAdmin(false);
  };

  const handleInvestorLogin = () => {
    setIsInvestorLoggedIn(true);
    setShowInvestors(false);
  };

  const handleInvestorLogout = () => {
    localStorage.removeItem('investorsToken');
    setIsInvestorLoggedIn(false);
    setShowInvestors(false);
  };

  const handleBackToMain = () => {
    setShowInvestors(false);
    setIsInvestorLoggedIn(false);
  };

  const handlePrivateAccessGranted = () => {
    setHasPrivateAccess(true);
    // S'assurer qu'on n'affiche pas l'admin par défaut
    setShowAdmin(false);
    setIsAdminLoggedIn(false);
  };

  // Si l'accès privé n'est pas accordé, afficher la page d'authentification
  if (!hasPrivateAccess) {
    return <PrivateAccessGate onAccessGranted={handlePrivateAccessGranted} />;
  }

  // Show investors portal if logged in
  if (isInvestorLoggedIn) {
    return <InvestorsPortal onLogout={handleInvestorLogout} onBack={handleBackToMain} />;
  }

  // Show investors login if triggered
  if (showInvestors) {
    return <InvestorsLogin onLogin={handleInvestorLogin} onClose={() => setShowInvestors(false)} />;
  }

  // Show admin dashboard if logged in
  if (isAdminLoggedIn) {
    return <AdminDashboard onLogout={handleAdminLogout} />;
  }

  // Show admin login if triggered
  if (showAdmin) {
    return <AdminLogin onLogin={handleAdminLogin} />;
  }

  // Show main website
  return (
    <div className="App">
      <Header />
      <HeroSection />
      <AIStorySection />
      <FavoriteStoriesSection />
      <LullabySection />
      <AnimatedStoriesSection />
      <AppFeaturesSection />
      <TestimonialsSection />
      <SupportersSection />
      <FAQSection />
      <SimplifiedFooter onAdminAccess={handleAdminAccess} />
    </div>
  );
}

export default App;